import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { getManualOnlyApi, ManualOnlyState, setManualOnlyApi } from "../../api/fastApi";

/**
 * Режим «Без автоправок — только ручная работа» для открытой пары.
 *
 * Флаг лежит на сервере (см. api/fastApi/manualOnly): редактор его только читает и
 * переключает. Пока ответа нет — или ручка ответила отказом, например ещё не выкачена, —
 * режим считается ВЫКЛЮЧЕННЫМ: для всех, кто его не включал, редактор ведёт себя ровно как
 * раньше.
 *
 * Переключают флаг не только отсюда: вкладка другого переводчика, админ, сборка
 * (translate-auto-generated `POST /projects/{media}/manual_only`). Поэтому флаг перечитывается —
 * с каждой загрузкой данных папок (сервер кладёт его в каждую папку `get_all_data_for_editing`),
 * при возврате во вкладку и раз в `pollMs`, пока вкладка видна. Вкладка, прочитавшая его один
 * раз при открытии, продолжала бы раскладывать соседей по-автоматному (зазор 50 мс, подгонка
 * скорости на сервере) и сохранять это как правку человека. В самом режиме соседи тоже
 * сдвигаются — но только наехавшие и только позже (правило владельца 01.10.2026, см.
 * cascadeNeighbours в провайдере). Мерку дублей для этого (`dub_ms`) сервер кладёт в данные
 * папок, только если пара была ручной, когда их читали; поэтому о каждой смене флага хук
 * сообщает провайдеру (`onFlagChange`) вместе с тем, откуда пришла весть, — и включённый не
 * загрузкой данных режим провайдер начинает с перечитывания папки.
 *
 * Вестей о флаге три — чтение, переключение и загрузка данных, — и приходят они вразнобой.
 * Каждая берёт номер, и действует только самая свежая: номер чтения и загрузки — на момент
 * ВЫХОДА запроса, номер переключения — на момент ОТВЕТА. Так чтение, ушедшее до
 * переключения и вернувшееся после, не откатывает только что включённый режим.
 */
export interface UseManualOnlyArgs {
    mediaId: string;
    /** Сервер редактора выбран (у эфира это свой адрес) — до этого спрашивать рано. */
    ready: boolean;
    /** Целевой язык пары в проводном виде; пустой — пары нет (открыт один оригинал). */
    lang: string;
    /** Исходный язык пары в проводном виде. */
    languageFrom: string;
    /**
     * Что сделать ДО переключения: сохранить отложенные правки. Сделанные при выключенном
     * режиме должны уйти на сервер по его прежним правилам, а не по новым.
     */
    beforeChange?: () => Promise<void>;
    /** Как часто перечитывать флаг, пока вкладка видна; 0 — не перечитывать по времени. */
    pollMs?: number;
    /**
     * Флаг пары сменился (включился или выключился) — и откуда пришла весть (см.
     * ManualOnlyFlagSource). Зовётся сразу, как весть легла, до перерисовки: провайдер успевает
     * закрыть раздвижку, пока в данных папок нет мерки дублей (`dub_ms`).
     */
    onFlagChange?: (on: boolean, source: ManualOnlyFlagSource) => void;
}

/**
 * Откуда пришла весть о флаге:
 *   * `read` — чтение флага (при открытии, по возврату во вкладку, раз в `pollMs`);
 *   * `set` — ответ на переключение из этой вкладки;
 *   * `data` — загрузка данных папок, заменившая их целиком: флаг пришёл ВМЕСТЕ с данными,
 *     и в ручной паре в них уже есть мерка дублей сервера (`dub_ms`);
 *   * `merge` — дозагрузка живого эфира: новые реплики дописываются, а прежние остаются
 *     прежними — мерки у них нет, как и после `read`/`set`.
 */
export type ManualOnlyFlagSource = "read" | "set" | "data" | "merge";

/** Пара, к которой относится загрузка данных: те же проводные коды, что у хука. */
export interface ManualOnlyPair {
    mediaId: string;
    lang: string;
    languageFrom: string;
}

export interface ManualOnlySetOptions {
    streamAck?: boolean;
}

export interface ManualOnlyControl {
    /** Действующий режим — по нему провайдер решает, делать ли автоправку. */
    manualOnly: boolean;
    /** Тот же режим для колбэков, которые иначе замкнули бы устаревшее значение. */
    manualOnlyRef: MutableRefObject<boolean>;
    /** Последнее, что известно от сервера; null — ещё не знаем. */
    state: ManualOnlyState | null;
    /** У открытого экрана есть пара — только к ней режим и относится. */
    applicable: boolean;
    loading: boolean;
    error: string | null;
    /** `options.streamAck` — переводчик подтвердил остановку живого дубляжа пары (см. setManualOnlyApi). */
    setManualOnly: (
        on: boolean,
        reason?: string,
        options?: ManualOnlySetOptions,
    ) => Promise<{ data?: ManualOnlyState; error?: string }>;
    refresh: () => Promise<void>;
    /**
     * Загрузка данных папок уходит: вернёт, чем отметить её ответ. Флаг из ответа ляжет,
     * только если с момента выхода этой загрузки ничего свежее не пришло.
     * `undefined` в ответе — сервер флага не прислал (старый): это не «выключено», а «не знаю».
     * `source` — `data` (данные заменяются целиком) или `merge` (дозагрузка эфира).
     */
    beginDataRead: (pair: ManualOnlyPair, source?: "data" | "merge") => (flag: boolean | undefined) => void;
}

/** Перечитывать флаг раз в полминуты: GET маленького файла, а окно чужой автоправки — не шире. */
export const MANUAL_ONLY_POLL_MS = 30000;

/**
 * Флаг пары из ответа `get_all_data_for_editing`: сервер кладёт `manual_only` в каждую папку.
 * Нет его ни в одной — сервер старый или папок нет: `undefined`, а не «выключено».
 */
export const manualOnlyFlagOfFolders = (folders: unknown): boolean | undefined => {
    if (!Array.isArray(folders)) return undefined;
    let flag: boolean | undefined;
    for (const folder of folders) {
        const value = folder && typeof folder === "object" ? (folder as { manual_only?: unknown }).manual_only : undefined;
        if (value === true) return true;
        if (value === false) flag = false;
    }
    return flag;
};

/** Кто переключил: имя входа редактора, если оно есть. Сервер пишет его в `set_by`. */
const editorUser = () => {
    try {
        const stored = JSON.parse(localStorage.getItem("torsunov_user") || "null");
        if (stored && typeof stored.username === "string" && stored.username) {
            return `editor:${stored.username}`;
        }
    } catch {
        /* нет входа или испорченная запись — подписываемся просто редактором */
    }
    return "editor";
};

const keyOf = ({ mediaId, lang, languageFrom }: ManualOnlyPair) => `${mediaId}|${lang}|${languageFrom}`;

export function useManualOnly({
    mediaId,
    ready,
    lang,
    languageFrom,
    beforeChange,
    pollMs = MANUAL_ONLY_POLL_MS,
    onFlagChange,
}: UseManualOnlyArgs): ManualOnlyControl {
    const [state, setState] = useState<ManualOnlyState | null>(null);
    // Последнее легшее состояние — синхронно, чтобы смена флага была видна в момент вести, а не
    // после перерисовки (onFlagChange).
    const stateRef = useRef<ManualOnlyState | null>(null);
    const onFlagChangeRef = useRef(onFlagChange);
    onFlagChangeRef.current = onFlagChange;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const applicable = Boolean(mediaId && lang && lang !== languageFrom);

    // Ответ, пришедший для прежней пары (переключили язык, лекцию), не должен лечь на новую.
    const pairKey = keyOf({ mediaId, lang, languageFrom });
    const pairKeyRef = useRef(pairKey);
    pairKeyRef.current = pairKey;

    const manualOnly = applicable && Boolean(state?.manual_only);
    const manualOnlyRef = useRef(manualOnly);
    manualOnlyRef.current = manualOnly;

    const beforeChangeRef = useRef(beforeChange);
    beforeChangeRef.current = beforeChange;

    // Номера вестей о флаге (см. шапку): выданный последним и последний легший.
    const ticketRef = useRef(0);
    const appliedRef = useRef(0);
    const accept = useCallback(
        (
            ticket: number,
            key: string,
            next: (prev: ManualOnlyState | null) => ManualOnlyState,
            source: ManualOnlyFlagSource,
        ) => {
            if (pairKeyRef.current !== key || ticket <= appliedRef.current) return false;
            appliedRef.current = ticket;
            const prev = stateRef.current;
            const value = next(prev);
            stateRef.current = value;
            setState(value);
            const was = Boolean(prev?.manual_only);
            const now = Boolean(value.manual_only);
            if (was !== now) onFlagChangeRef.current?.(now, source);
            return true;
        },
        [],
    );

    /** `quiet` — перечитывание в фоне: без крутилки у выключателя и без ошибки под ним. */
    const read = useCallback(
        async (quiet: boolean) => {
            if (!applicable || !ready) return;
            const key = pairKey;
            const ticket = ++ticketRef.current;
            if (!quiet) setLoading(true);
            try {
                const next = await getManualOnlyApi({ mediaId, lang, languageFrom });
                if (accept(ticket, key, () => next, "read") && !quiet) setError(null);
            } catch (e) {
                // Фоновая неудача — не новость о флаге: остаётся последнее известное.
                if (!quiet && pairKeyRef.current === key) setError(e instanceof Error ? e.message : String(e));
            } finally {
                if (!quiet && pairKeyRef.current === key) setLoading(false);
            }
        },
        [applicable, ready, pairKey, mediaId, lang, languageFrom, accept],
    );

    const refresh = useCallback(() => read(false), [read]);

    useEffect(() => {
        stateRef.current = null;
        setState(null);
        setError(null);
        setLoading(false);
        void refresh();
    }, [refresh]);

    // Вернулись во вкладку, вкладка стала видна, прошло `pollMs` — перечитать.
    useEffect(() => {
        if (!applicable || !ready) return undefined;
        const reread = () => {
            if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
            void read(true);
        };
        window.addEventListener("focus", reread);
        document.addEventListener("visibilitychange", reread);
        const timer = pollMs > 0 ? window.setInterval(reread, pollMs) : undefined;
        return () => {
            window.removeEventListener("focus", reread);
            document.removeEventListener("visibilitychange", reread);
            if (timer !== undefined) window.clearInterval(timer);
        };
    }, [applicable, ready, read, pollMs]);

    const beginDataRead = useCallback(
        (pair: ManualOnlyPair, source: "data" | "merge" = "data") => {
            const ticket = ++ticketRef.current;
            const key = keyOf(pair);
            const isPair = Boolean(pair.mediaId && pair.lang && pair.lang !== pair.languageFrom);
            return (flag: boolean | undefined) => {
                if (!isPair || flag === undefined) return;
                // Тот же флаг — оставляем, кто и когда его переключил: данные папок этого не несут.
                accept(ticket, key, (prev) => (prev && prev.manual_only === flag ? prev : { manual_only: flag }), source);
            };
        },
        [accept],
    );

    const setManualOnly = useCallback(
        async (on: boolean, reason?: string, options?: ManualOnlySetOptions) => {
            if (!applicable) return { error: "режим относится к паре языков — откройте перевод" };
            const key = pairKey;
            try {
                await beforeChangeRef.current?.();
            } catch (e) {
                const message = "Не удалось сохранить отложенные правки — режим не переключён";
                setError(message);
                return { error: message };
            }
            setLoading(true);
            try {
                const next = await setManualOnlyApi({
                    mediaId,
                    lang,
                    languageFrom,
                    manualOnly: on,
                    by: editorUser(),
                    reason: reason ?? (on ? "включено в редакторе" : "выключено в редакторе"),
                    ...(options?.streamAck ? { streamAck: true } : {}),
                });
                // Номер — на момент ответа: всё, что ушло раньше, о флаге знает меньше.
                if (accept(++ticketRef.current, key, () => next, "set")) setError(null);
                return { data: next };
            } catch (e) {
                const message = e instanceof Error ? e.message : String(e);
                if (pairKeyRef.current === key) setError(message);
                return { error: message };
            } finally {
                if (pairKeyRef.current === key) setLoading(false);
            }
        },
        [applicable, pairKey, mediaId, lang, languageFrom, accept],
    );

    return {
        manualOnly,
        manualOnlyRef,
        state,
        applicable,
        loading,
        error,
        setManualOnly,
        refresh,
        beginDataRead,
    };
}
